import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { X, CheckSquare } from "lucide-react";
import { toast } from "react-toastify";
import ReCAPTCHA from "react-google-recaptcha";

const GrievanceForm = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    contactNumber: "",
    pageUrl: "",
    complaintReasons: [],
    otherReason: "",
    description: "",
    captcha: null,
  });

  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const fileInputRef = useRef(null);

  const complaintReasons = [
    {
      id: "violates_law",
      label: "Violates applicable laws",
    },
    {
      id: "data_misuse",
      label: "Data misuse, breach or leakage",
    },
    {
      id: "delay_deficiency",
      label: "Delay or deficiency in service",
    },
    {
      id: "non_transparent_selection",
      label: "Non-transparent selection criteria",
    },
    {
      id: "infringes_ip",
      label: "Infringes intellectual property rights",
    },
    {
      id: "incorrect_scoring",
      label: "Incorrect scoring or evaluation errors",
    },
    {
      id: "unfair_practices",
      label: "Unfair practices or misleading information",
    },
    {
      id: "lack_human_review",
      label: "Lack of human review in candidate evaluation",
    },
    {
      id: "consent_issues",
      label: "Consent not obtained or improperly obtained",
    },
    {
      id: "failed_submission",
      label: "Failed or incomplete submission of applications",
    },
    {
      id: "platform_malfunction",
      label: "Loss of opportunity due to platform malfunction",
    },
    {
      id: "auto_rejection",
      label: "Auto-generated rejection without proper explanation",
    },
    {
      id: "incorrect_filtering",
      label: "Qualified candidates being incorrectly filtered out",
    },
    {
      id: "data_beyond_purpose",
      label: "Use of candidate data beyond intended hiring purpose",
    },
    {
      id: "excessive_data_collection",
      label: "Collection of excessive or irrelevant personal data",
    },
    {
      id: "unfair_shortlisting",
      label: "Unfair shortlisting or rejection without valid reasons",
    },
    {
      id: "system_crashes",
      label: "System crashes or technical failures during application",
    },
    {
      id: "unauthorized_sharing",
      label: "Unauthorized sharing of personal data with third parties",
    },
    {
      id: "bias_exclusion",
      label: "Bias or unfair exclusion due to automated systems/algorithms",
    },
    {
      id: "threatens_sovereignty",
      label: "Threatens the sovereignty, security, public order of a jurisdiction or constitutes a violation of applicable local laws and regulations",
    },
    {
      id: "discriminatory_hiring",
      label: "Discriminatory hiring practices (based on gender, religion, caste, age, disability, etc.)",
    },
    {
      id: "malware",
      label: "Contains malware or harmful code file or program designed to interrupt, destroy or limit the functionality of any computer resource",
    },
    {
      id: "false_misleading",
      label: "Content is false and untrue and is written or published in any form, with the intent to mislead or harass a person, entity or agency for financial gain or to cause any injury to any person",
    },
    {
      id: "obscene_content",
      label: "Any content that is obscene, pornographic, paedophilic, invasive of privacy, abusive, harassing, discriminatory, or promotes enmity or unlawful activities (including money laundering or harmful online gaming)",
    },
    {
      id: "others",
      label: "Others (including Consumer Complaints)",
    },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCaptchaChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      captcha: value,
    }));
  };

  const handleCheckboxChange = (reasonId) => {
    setFormData((prev) => {
      const reasons = prev.complaintReasons.includes(reasonId)
        ? prev.complaintReasons.filter((id) => id !== reasonId)
        : [...prev.complaintReasons, reasonId];
      return { ...prev, complaintReasons: reasons };
    });
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    const allowedTypes = ["gif", "png", "jpg", "jpeg", "pdf"];
    const fileExtension = selectedFile.name.split(".").pop().toLowerCase();

    if (!allowedTypes.includes(fileExtension)) {
      setFileError("Accepted formats: GIF, PNG, JPG, PDF or JPEG only");
      return;
    }

    if (selectedFile.size > 2 * 1024 * 1024) {
      setFileError("File must not exceed 2 MB");
      return;
    }

    setFile(selectedFile);
    setFileError("");
  };

  const handleRemoveFile = () => {
    setFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.name.trim()) {
      toast.error("Please enter your name");
      return;
    }

    if (!formData.email.trim()) {
      toast.error("Please enter your email address");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    if (!formData.contactNumber.trim()) {
      toast.error("Please enter your contact number");
      return;
    }

    if (!formData.pageUrl.trim()) {
      toast.error("Please enter the page URL");
      return;
    }

    if (formData.complaintReasons.length === 0) {
      toast.error("Please select at least one reason for complaint");
      return;
    }

    if (formData.complaintReasons.includes("others") && !formData.otherReason.trim()) {
      toast.error("Please describe your 'Other' reason");
      return;
    }

    if (!formData.description.trim()) {
      toast.error("Please provide a detailed description");
      return;
    }

    if (!formData.captcha) {
      toast.error("Please complete the captcha verification");
      return;
    }

    setLoading(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append("name", formData.name);
      formDataToSend.append("email", formData.email);
      formDataToSend.append("contactNumber", formData.contactNumber);
      formDataToSend.append("pageUrl", formData.pageUrl);
      formDataToSend.append("complaintReasons", JSON.stringify(formData.complaintReasons));
      formDataToSend.append("description", formData.description);
      formDataToSend.append("captcha", formData.captcha);
      
      if (formData.otherReason) {
        formDataToSend.append("otherReason", formData.otherReason);
      }
      
      if (file) {
        formDataToSend.append("file", file);
      }

      const apiUrl = process.env.REACT_APP_API_HOST;
      const response = await fetch(`${apiUrl}/api/submit-grievance`, {
        method: "POST",
        body: formDataToSend,
      });

      const result = await response.json();

      if (response.ok) {
        toast.success(result.message || "Your grievance has been submitted successfully.");
        // Reset form
        setFormData({
          name: "",
          email: "",
          contactNumber: "",
          pageUrl: "",
          complaintReasons: [],
          otherReason: "",
          description: "",
          captcha: null,
        });
        setFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      } else {
        toast.error(result.detail || "Failed to submit grievance. Please try again.");
      }
    } catch (error) {
      console.error("Submission error:", error);
      toast.error("An error occurred. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen w-full p-4 md:p-8 relative"
      style={{
        backgroundImage: 'url("/white-pink_bg_img.png")',
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <style>
        {`
          input:-webkit-autofill,
          input:-webkit-autofill:hover, 
          input:-webkit-autofill:focus,
          textarea:-webkit-autofill,
          textarea:-webkit-autofill:hover,
          textarea:-webkit-autofill:focus {
            -webkit-text-fill-color: black !important;
            transition: background-color 5000s ease-in-out 0s;
          }
        `}
      </style>
      <div className="max-w-4xl mx-auto">
        {/* Header Section */}
        <div className="mb-8 flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-6 md:pt-4">
          <img
            src="/genie-demo-logo-black.png"
            alt="GenieHire Logo"
            className="h-10 md:h-14 w-auto object-contain"
          />
          <div className="flex-1 w-full border-b-2 border-gray-200 dark:border-gray-700 pb-2">
            <h1 className="text-left text-lg md:text-3xl font-bold text-gray-600 dark:text-gray-600 leading-tight">
              Grievance Form - for Consumer Complaints
            </h1>
          </div>
        </div>

        <div className="flex justify-end mb-6">
          <p className="text-sm text-gray-600 dark:text-gray-600">
            Compulsory Fields<span className="text-red-500">*</span>
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Your Name */}
          <div className="flex flex-col md:flex-row md:items-center gap-2">
            <label className="w-full md:w-1/3 text-sm font-medium text-textPrimary dark:text-gray-200">
              <span className="text-red-500">*</span> Your Name:
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="w-full md:w-2/3 px-4 py-3 border border-gray-300 dark:border-[#5A4479] rounded-lg bg-white dark:bg-transparent dark:text-[#CCCCCC] text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#5A4479] focus:border-transparent transition-all duration-200"
              placeholder="Enter your name"
            />
          </div>

          {/* Your E-Mail ID */}
          <div className="flex flex-col md:flex-row md:items-center gap-2">
            <label className="w-full md:w-1/3 text-sm font-medium text-textPrimary dark:text-gray-200">
              <span className="text-red-500">*</span> Your E-Mail ID:
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full md:w-2/3 px-4 py-3 border border-gray-300 dark:border-[#5A4479] rounded-lg bg-white dark:bg-transparent dark:text-[#CCCCCC] text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#5A4479] focus:border-transparent transition-all duration-200"
              placeholder="Enter your email"
            />
          </div>

          {/* Your Contact Number */}
          <div className="flex flex-col md:flex-row md:items-center gap-2">
            <label className="w-full md:w-1/3 text-sm font-medium text-textPrimary dark:text-gray-200">
              <span className="text-red-500">*</span> Your Contact Number:
            </label>
            <input
              type="tel"
              name="contactNumber"
              value={formData.contactNumber}
              onChange={handleChange}
              className="w-full md:w-2/3 px-4 py-3 border border-gray-300 dark:border-[#5A4479] rounded-lg bg-white dark:bg-transparent dark:text-[#CCCCCC] text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#5A4479] focus:border-transparent transition-all duration-200"
              placeholder="Enter your contact number"
            />
          </div>

          {/* Page URL */}
          <div className="flex flex-col md:flex-row md:items-start gap-2">
            <label className="w-full md:w-1/3 text-sm font-medium text-textPrimary dark:text-gray-200 pt-3">
              <span className="text-red-500">*</span> Please share the link (URL) of the Page which you are reporting against:
            </label>
            <input
              type="url"
              name="pageUrl"
              value={formData.pageUrl}
              onChange={handleChange}
              className="w-full md:w-2/3 px-4 py-3 border border-gray-300 dark:border-[#5A4479] rounded-lg bg-white dark:bg-transparent dark:text-[#CCCCCC] text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#5A4479] focus:border-transparent transition-all duration-200"
              placeholder="https://example.com/page"
            />
          </div>

          {/* Reason for Complaint */}
          <div className="flex flex-col md:flex-row md:items-start gap-2">
            <label className="w-full md:w-1/3 text-sm font-medium text-textPrimary dark:text-gray-200 pt-3">
              <span className="text-red-500">*</span> Please tell us the reason for your complaint/concern. Choose an option which most closely matches with your concern. If you are unsure which option to choose, please select the last option. Thanks
            </label>
            <div className="w-full md:w-2/3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
                {complaintReasons.map((reason) => (
                  <div key={reason.id} className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      id={reason.id}
                      checked={formData.complaintReasons.includes(reason.id)}
                      onChange={() => handleCheckboxChange(reason.id)}
                      className="w-4 h-4 mt-1 accent-blue-500 flex-shrink-0"
                    />
                    <label
                      htmlFor={reason.id}
                      className="text-sm text-textPrimary dark:text-gray-300 cursor-pointer leading-relaxed"
                    >
                      {reason.label}
                    </label>
                  </div>
                ))}
              </div>
              {formData.complaintReasons.includes("others") && (
                <div className="mt-4">
                  <textarea
                    name="otherReason"
                    value={formData.otherReason}
                    onChange={handleChange}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-[#5A4479] rounded-lg bg-white dark:bg-transparent dark:text-[#CCCCCC] text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#5A4479] focus:border-transparent transition-all duration-200 resize-none"
                    placeholder="Please describe your reason..."
                  />
                </div>
              )}
            </div>
          </div>

          {/* Detailed Description */}
          <div className="flex flex-col md:flex-row md:items-start gap-2">
            <label className="w-full md:w-1/3 text-sm font-medium text-textPrimary dark:text-gray-200 pt-3">
              <span className="text-red-500">*</span> Please describe your complaint/concern in detail:
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={6}
              className="w-full md:w-2/3 px-4 py-3 border border-gray-300 dark:border-[#5A4479] rounded-lg bg-white dark:bg-transparent dark:text-[#CCCCCC] text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#5A4479] focus:border-transparent transition-all duration-200 resize-none"
              placeholder="Please provide detailed information about your complaint..."
            />
          </div>

          {/* File Upload */}
          <div className="flex flex-col md:flex-row md:items-start gap-2">
            <label className="w-full md:w-1/3 text-sm font-medium text-textPrimary dark:text-gray-200 pt-3">
              Please upload any supporting document(s) pertaining to the issue you are reporting:
            </label>
            <div className="w-full md:w-2/3">
              <div className="flex items-center gap-4">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept=".gif,.png,.jpg,.jpeg,.pdf"
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="px-4 py-2 border border-gray-300 dark:border-[#5A4479] rounded-lg bg-gray-100 dark:bg-gray-700 text-textPrimary dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  Choose File
                </button>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {file ? file.name : "No file chosen"}
                </span>
                {file && (
                  <button
                    type="button"
                    onClick={handleRemoveFile}
                    className="p-1 text-red-500 hover:text-red-700 transition-colors"
                  >
                    <X size={20} />
                  </button>
                )}
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Accepted formats: GIF, PNG, JPG, PDF or JPEG
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                File must not be in use.
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Maximum File Size Limit: 2 MB
              </p>
              {fileError && (
                <p className="text-xs text-red-500 mt-1">{fileError}</p>
              )}
            </div>
          </div>

          {/* reCAPTCHA */}
          <div className="flex flex-col md:flex-row md:items-start gap-2">
            <label className="w-full md:w-1/3 text-sm font-medium text-textPrimary dark:text-gray-200 pt-3">
              {/* Empty label for alignment */}
            </label>
            <div className="w-full md:w-2/3">
              <ReCAPTCHA
                sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                onChange={handleCaptchaChange}
              />
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-center pt-4">
            <button
              type="submit"
              disabled={loading}
              className={`button px-8 py-3 rounded-full text-white font-medium transition-all duration-200 ${loading ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-90'}`}
            >
              <span className="text-white">
                {loading ? "Submitting..." : "Submit"}
              </span>
            </button>
          </div>
        </form>

        {/* Footer Section */}
        <div className="mt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-[#707070] border-t border-gray-200 dark:border-gray-700/50 pt-8">
          <p>Copyright© 2025 NSquare Innovators Inc. All rights reserved.</p>
          <div className="flex gap-6 font-medium">
            <Link to="/privacy-policy" className="hover:text-gray-900 dark:hover:text-gray-300 transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms-of-use" className="hover:text-gray-900 dark:hover:text-gray-300 transition-colors">
              Terms of Use
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GrievanceForm;